import { Logger } from '../../Logger';
import { AuthStorage } from '../../AuthStorage';
import { AuthStorageWrapper } from '../../AuthStorageWrapper';
import { BaseTableEntry } from '../../TypeDefinitions';
import { logConstants } from '../../../utils/Constants';
import { StringMap } from '../../../utils/TypeDefinitions';
import { storageConstants } from '../../../auth/AuthConstants';

export interface CredentialsTableEntry extends BaseTableEntry {
	authServiceUrl: string;
	credentials: StringMap;
}

export interface CredentialsTableKey {
	authServiceUrl: string;
	referenceUri: string;
}

export default class CredentialsStore {
	private credentialsStorage: AuthStorage;

	private constructor(readonly tableName: string) {}

	static async createInstance(tableName: string): Promise<CredentialsStore> {
		const instance = new CredentialsStore(tableName);
		instance.credentialsStorage = (
			await AuthStorageWrapper.getInstance()
		).getPersistentStorage();
		return instance;
	}

	async read(authServiceUrl: string, referenceUri: string): Promise<StringMap> {
		try {
			const credentialsTableEntry: CredentialsTableEntry =
				(await this.credentialsStorage.readEntry(this.tableName, [
					authServiceUrl,
					referenceUri,
				])) as CredentialsTableEntry;
			return credentialsTableEntry ? credentialsTableEntry.credentials : {};
		} catch (e) {
			Logger.getInstance().write(
				logConstants.INFO,
				`Error while reading credentials in ${this.tableName} store for ${authServiceUrl}: ${e.message}`
			);
		}

		return {};
	}

	async save(
		authServiceUrl: string,
		referenceUri: string,
		credentials: StringMap
	): Promise<void> {
		try {
			const credentialsTableEntry: CredentialsTableEntry = {
				authServiceUrl: authServiceUrl,
				credentials: credentials,
				referenceUri: referenceUri,
			};
			await this.credentialsStorage.updateEntry(this.tableName, credentialsTableEntry);
		} catch (e) {
			Logger.getInstance().write(
				logConstants.INFO,
				`Error while saving credentials in ${this.tableName} store for ${authServiceUrl}: ${e.message}`
			);
		}
	}

	async erase(authServiceUrl: string, referenceUri: string): Promise<void> {
		Logger.getInstance().write(
			logConstants.INFO,
			`Deleting credentials in ${this.tableName} store for referenceUri ${referenceUri}, authServiceUrl ${authServiceUrl}`
		);
		try {
			await this.credentialsStorage.deleteEntry(this.tableName, [
				authServiceUrl,
				referenceUri,
			]);
		} catch (e) {
			Logger.getInstance().write(
				logConstants.INFO,
				`Error while deleting credentials in ${this.tableName} store for ${authServiceUrl}: ${e.message}`
			);
		}
	}

	async eraseForStore(referenceUri: string): Promise<void> {
		try {
			const credentials = (await this.credentialsStorage.readAll(
				this.tableName,
				storageConstants.REFERENCE_URI,
				referenceUri
			)) as CredentialsTableEntry[];
			if (credentials) {
				Logger.getInstance().write(
					logConstants.INFO,
					`Deleting credentials in ${this.tableName} store for referenceUri ${referenceUri}`
				);
				for (const credentialEntry of credentials) {
					await this.credentialsStorage.deleteEntry(this.tableName, [
						credentialEntry.authServiceUrl,
						credentialEntry.referenceUri,
					]);
				}
			}
		} catch (e) {
			Logger.getInstance().write(
				logConstants.INFO,
				`Error while deleting credentials in ${this.tableName} store for ${referenceUri}: ${e.message}`
			);
		}
	}

	async getAllKeys(): Promise<CredentialsTableKey[]> {
		try {
			const keys = (await this.credentialsStorage.readAllKeys(
				this.tableName
			)) as string[][];

			const credentialsTableKeys: CredentialsTableKey[] = [];
			if (keys) {
				for (const key of keys) {
					credentialsTableKeys.push({ authServiceUrl: key[0], referenceUri: key[1] });
				}
			}
			return credentialsTableKeys;
		} catch (e) {
			Logger.getInstance().write(
				logConstants.INFO,
				'Error during getAllKeys: ' + e.message
			);
		}
	}

	async eraseAll(): Promise<void> {
		Logger.getInstance().write(
			logConstants.INFO,
			`Deleting all credentials in ${this.tableName} store.`
		);
		try {
			await this.credentialsStorage.clearObjectStore(this.tableName);
		} catch (e) {
			Logger.getInstance().write(
				logConstants.INFO,
				`Error while clearing all credentials in ${this.tableName} store: ${e.message}`
			);
		}
	}
}
